import React from 'react';
import { motion } from 'framer-motion';
import { User, AlertCircle, CheckCircle } from 'lucide-react';

const ProfileInsightsPanel = ({ profileSettings, behavioralProfile, cognitiveProfile }) => {
  if (!profileSettings || !profileSettings.profile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No profile settings available</p>
      </motion.div>
    );
  }

  const { profile } = profileSettings;

  // Compare self-reported vs analyzed data
  const comparisons = [
    {
      aspect: 'Learning Style',
      selfReported: profile.learningPreferences?.[0] || 'Not specified',
      analyzed: cognitiveProfile?.learningStyle || 'Unknown',
      match: profile.learningPreferences?.[0]?.toLowerCase() === cognitiveProfile?.learningStyle
    },
    {
      aspect: 'Study Consistency',
      selfReported: profile.studyHabits || 'Not specified',
      analyzed: behavioralProfile?.studyConsistency?.pattern || 'Unknown',
      match: true // Simplified comparison
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-indigo-600 rounded-lg">
          <User className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Profile Insights</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Self-reported vs. analyzed data</p>
        </div>
      </div>

      {/* Comparisons */}
      <div className="space-y-4">
        {comparisons.map((comparison, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{comparison.aspect}</h4>
              {comparison.match ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-gray-400 mb-1">You said:</p>
                <p className="font-medium text-gray-900 dark:text-white">{comparison.selfReported}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 mb-1">We found:</p>
                <p className="font-medium text-gray-900 dark:text-white">{comparison.analyzed}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Goals */}
      {profile.goals && profile.goals.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Your Goals</h4>
          <div className="space-y-2">
            {profile.goals.map((goal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300">{goal.goal}</span>
                <span className="text-xs font-medium text-blue-600">{goal.progress}%</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ProfileInsightsPanel;
